import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { CreditCardIcon, CheckCircleIcon, ExclamationTriangleIcon, UsersIcon, BanknotesIcon, GlobeAmericasIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface Package {
  _id: string;
  name: string;
  priceUSD: number;
  priceAUD: number;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
  description: string;
  packageCode: string;
  nights: number;
  actionType: 'enquire' | 'book';
  enquireEnabled: boolean;
  bookEnabled: boolean;
}

declare global {
  interface Window {
    FlywirePayment: any;
  }
}

const BookNowPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const packageId = searchParams.get('package');
  const priceType = searchParams.get('priceType') as 'standard' | 'deal' || 'standard';
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>(packageId || '');
  const [selectedPriceType, setSelectedPriceType] = useState<'standard' | 'deal'>(priceType);
  const [manualAmount, setManualAmount] = useState<string>('');
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [settings, setSettings] = useState<any>(null);
  const [currency, setCurrency] = useState<'USD' | 'AUD'>('AUD');
  const [isManualPayment, setIsManualPayment] = useState(false);

  // Initialize reCAPTCHA
  const { executeRecaptcha, verifyRecaptchaToken, enabled: recaptchaEnabled } = useRecaptcha(settings?.recaptcha);

  // Customer details
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+61',
    country: 'AU'
  });
  // Fetch settings and packages
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Check for cached settings first to avoid rate limiting
        const cachedSettings = localStorage.getItem('baligetaway-settings');
        const cacheTime = localStorage.getItem('baligetaway-settings-time');
        const fiveMinutes = 5 * 60 * 1000;

        if (cachedSettings && cacheTime && (Date.now() - parseInt(cacheTime)) < fiveMinutes) {
          console.log('Using cached settings to avoid rate limiting');
          const parsedSettings = JSON.parse(cachedSettings);
          setSettings(parsedSettings);
          return;
        }

        const response = await fetch('/api/settings/public');
        if (response.ok) {
          const settingsData = await response.json();
          setSettings(settingsData);
          // Cache the settings
          localStorage.setItem('baligetaway-settings', JSON.stringify(settingsData));
          localStorage.setItem('baligetaway-settings-time', Date.now().toString());
        } else {
          throw new Error(`Settings API returned ${response.status}`);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Use reliable defaults if settings fetch fails
        const fallbackSettings = {
          flywire: { enabled: true, environment: 'demo' },
          recaptcha: { enabled: false, siteKey: '', scoreThreshold: 0.5 }
        };
        setSettings(fallbackSettings);
        console.log('Using fallback settings due to API error');
      }
    };

    const fetchPackages = async () => {
      try {
        // Use relative URL since we're running both frontend and backend
        const response = await axios.get('/api/packages?live=true');
        const allPackages = response.data.packages || [];

        console.log('📦 Fetched packages:', allPackages.length, 'total packages');

        // Filter packages to only show bookable ones (not enquiry-only)
        const bookablePackages = allPackages.filter((pkg: Package) =>
          pkg.bookEnabled && !(pkg.actionType === 'enquire' && !pkg.bookEnabled)
        );

        console.log('📦 Bookable packages:', bookablePackages.length, 'bookable packages');
        console.log('📦 Package details:', allPackages.map((pkg: Package) => ({
          name: pkg.name,
          bookEnabled: pkg.bookEnabled,
          actionType: pkg.actionType,
          isLive: (pkg as any).isLive
        })));

        setPackages(bookablePackages);

        // Handle preselected package logic
        if (packageId) {
          const selectedPkg = allPackages.find((pkg: Package) => pkg._id === packageId);
          if (selectedPkg) {
            // If package is enquiry-only, force manual payment and hide package tab
            if (selectedPkg.actionType === 'enquire' && !selectedPkg.bookEnabled) {
              setIsManualPayment(true);
              setSelectedPackageId('');
              setError('This package is only available for enquiries. Please use custom payment or contact us directly.');
            }
            // If package has booking enabled, show both tabs
            else if (selectedPkg.bookEnabled) {
              setIsManualPayment(false);
              setSelectedPackageId(packageId);
            }
          }
        }

        // If no bookable packages available, force manual payment
        if (bookablePackages.length === 0) {
          setIsManualPayment(true);
          setSelectedPackageId('');
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Don't set error for packages - allow manual payments to work
        console.warn('Packages failed to load, using manual payment mode');
        setPackages([]); // Empty array means only manual payments available
        setIsManualPayment(true); // Force manual payment mode
        setSelectedPackageId(''); // Clear any selected package
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    fetchPackages();
  }, [packageId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getPaymentAmount = () => {
    if (isManualPayment) return parseFloat(manualAmount) || 0;

    const selectedPackage = packages.find(p => p._id === selectedPackageId);
    if (!selectedPackage) return 0;

    // Use the price type selected by the user on the package detail page
    if (selectedPriceType === 'deal') {
      // Check for active last minute deal
      const dealPriceAUD = selectedPackage.lastMinuteDealPriceAUD;
      const dealPriceUSD = selectedPackage.lastMinuteDealPriceUSD;
      const hasDealPrice = currency === 'AUD'
        ? dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < selectedPackage.priceAUD
        : dealPriceUSD && dealPriceUSD > 0 && dealPriceUSD < selectedPackage.priceUSD;
      const isDealActive = selectedPackage.lastMinuteDealExpiryDate
        ? new Date(selectedPackage.lastMinuteDealExpiryDate) > new Date()
        : false;

      // Use deal price if active
      if (hasDealPrice && isDealActive) {
        return currency === 'USD'
          ? (dealPriceUSD || selectedPackage.priceUSD)
          : (dealPriceAUD || selectedPackage.priceAUD);
      }
    }

    // Use standard price (either selectedPriceType is 'standard' or deal is not available)
    return currency === 'USD' ? selectedPackage.priceUSD : selectedPackage.priceAUD;
  };

  // Helper functions for tab visibility
  const shouldShowPackageTab = (): boolean => {
    // Show package tab if there are bookable packages available
    return packages.length > 0;
  };

  const shouldShowManualTab = (): boolean => {
    // Always show manual tab for custom payments
    return true;
  };

  const shouldShowBothTabs = (): boolean => {
    // Show both tabs if there are bookable packages
    return packages.length > 0;
  };

  // Get package info for payment
  const getPackageInfo = () => {
    if (!isManualPayment && selectedPackageId) {
      return packages.find(p => p._id === selectedPackageId);
    }
    return null;
  };

  // Handle form submit - Flywire payment only
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.phone.length < 8) {
      setError('Please enter a valid phone number');
      return;
    }

    const amount = getPaymentAmount();
    if (amount <= 0) {
      setError('Please enter a valid amount or select a package');
      return;
    }

    // Check if Flywire is enabled
    const flywireEnabled = settings?.flywire?.enabled ?? true;
    if (!flywireEnabled) {
      setError('Payment is not available. Please contact support.');
      return;
    }

    setProcessing(true);

    try {
      // Clean phone number (remove any spaces, dashes, parentheses)
      const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
      const amount = getPaymentAmount();

      // Determine package info
      let packageInfo: any = null;
      if (!isManualPayment && selectedPackageId) {
        packageInfo = packages.find(p => p._id === selectedPackageId);
      }

      // Prepare payment data
      const paymentData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: `${formData.countryCode}${cleanPhone}`,
        amount: amount,
        currency: currency,
        packageId: isManualPayment ? null : selectedPackageId,
        packageName: packageInfo ? packageInfo.name : 'Custom Payment',
        packageCode: packageInfo ? packageInfo.packageCode : 'CUSTOM'
      };

      // Check if settings are loaded, provide fallbacks if not
      if (!settings) {
        console.warn('Settings not loaded, using fallback configuration');
        // Don't block payment - use fallback settings
      }

      // Check if Flywire payment system is loaded
      if (typeof window.FlywirePayment === 'undefined') {
        setError('Flywire payment system not loaded. Please refresh the page.');
        setProcessing(false);
        return;
      }

      // Check if initiate function exists
      if (typeof window.FlywirePayment.initiate !== 'function') {
        setError('Flywire initiate function not available. Please refresh the page.');
        setProcessing(false);
        return;
      }

      // Get recipient code based on settings environment and currency
      let recipientCode: string;
      const flywireEnv = settings?.flywire?.environment || 'demo';
      const isLive = flywireEnv === 'live';

      console.log('🔧 Flywire Settings Debug:');
      console.log('  - settings.flywire:', settings?.flywire);
      console.log('  - environment:', flywireEnv);
      console.log('  - isLive:', isLive);

      if (isLive) {
        // Live environment - use live codes from settings or fallback
        recipientCode = currency === 'AUD'
          ? (settings?.flywire?.live?.audRecipientCode || 'FQD')
          : (settings?.flywire?.live?.usdRecipientCode || 'FQG');
      } else {
        // Demo environment - use demo codes from settings or fallback
        recipientCode = currency === 'AUD'
          ? (settings?.flywire?.demo?.audRecipientCode || 'IRG')
          : (settings?.flywire?.demo?.usdRecipientCode || 'IRG');
      }

      console.log('  - Currency:', currency);
      console.log('  - Recipient Code:', recipientCode);
      console.log('  - Flywire env (for API):', isLive ? 'prod' : 'demo');

      // reCAPTCHA validation if enabled (skip if settings not loaded)
      const recaptchaEnabledSafe = recaptchaEnabled && settings?.recaptcha?.enabled;
      if (recaptchaEnabledSafe) {
        try {
          console.log('Attempting reCAPTCHA validation...');
          const token = await executeRecaptcha('flywire_payment');
          const verification = await verifyRecaptchaToken(token, 'flywire_payment');

          if (!verification.success || (typeof verification.score === 'number' && verification.score < (settings?.recaptcha?.scoreThreshold || 0.5))) {
            console.warn('reCAPTCHA validation failed, but continuing with payment...');
            // Don't block payment for reCAPTCHA issues in development
            // throw new Error(verification.message || 'reCAPTCHA validation failed. Please try again.');
          }

          console.log('reCAPTCHA verified:', verification);
        } catch (recaptchaError: any) {
          console.warn('reCAPTCHA error:', recaptchaError);
          console.log('Continuing with payment despite reCAPTCHA issue...');
          // Don't block payment for reCAPTCHA issues
          // setError(recaptchaError.message || 'Security validation failed. Please try again.');
          // setProcessing(false);
          // return;
        }
      }

      // Generate unique transaction ID
      const transactionId = `TXN${Date.now()}`;

      // Construct return URL with all required parameters for booking creation
      const returnUrl = `${window.location.origin}/payment-success?status=pending&reference=${transactionId}&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(paymentData.packageName)}&packageId=${isManualPayment ? '' : selectedPackageId}&phone=${encodeURIComponent(paymentData.phone)}&paymentMethod=Flywire`;

      // Construct callback URL (webhook) - matches payment.js routes
      const callbackUrl = `${window.location.origin}/api/payment/webhook/${currency.toLowerCase()}`;

      // Flywire config matching the working PHP format
      const flywireConfig = {
        env: isLive ? 'prod' : 'demo',
        recipientCode: recipientCode,
        amount: parseFloat(amount.toFixed(2)),
        currency: currency.toUpperCase(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: paymentData.phone,
        recipientFields: {
          booking_reference: transactionId,
          web_source: window.location.hostname,
          package_name: paymentData.packageName,
          package_code: paymentData.packageCode
        },
        requestPayerInfo: true,
        requestRecipientInfo: false,
        returnUrl: returnUrl,
        callbackUrl: callbackUrl,
        callbackId: "BaliGetaway",
        callbackVersion: "2",
        onInvalidInput: (errors: any[]) => {
          const errorMessages = errors.map(error => error.msg || error.message).join('\n');
          setError(`Payment validation failed:\n${errorMessages}`);
          setProcessing(false);
        },
        // Handle successful payment completion
        onSuccess: async (result: any) => {
          console.log('✅ Flywire payment success:', result);
          try {
            // Extract Flywire payment ID from result
            const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
            
            // Create booking record immediately
            const bookingResponse = await axios.post('/api/bookings/create', {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: paymentData.phone,
              amount: amount,
              currency: currency,
              packageId: isManualPayment ? null : selectedPackageId,
              packageName: paymentData.packageName,
              transactionId: flywirePaymentId,
              paymentMethod: 'flywire'
            });
            
            console.log('✅ Booking created:', bookingResponse.data);
            
            // Redirect to success page with actual Flywire payment ID
            const successUrl = `/payment-success?status=success&transactionId=${flywirePaymentId}&reference=${bookingResponse.data.booking?.bookingCode || transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(paymentData.packageName)}&paymentMethod=Flywire`;
            navigate(successUrl);
          } catch (bookingError: any) {
            console.error('❌ Failed to create booking after payment:', bookingError);
            // Still redirect to success page even if booking creation fails
            const successUrl = `/payment-success?status=success&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(paymentData.packageName)}&paymentMethod=Flywire`;
            navigate(successUrl);
          }
        },
        // Handle payment completion (fallback)
        onComplete: async (result: any) => {
          console.log('✅ Flywire payment complete:', result);
          // onSuccess should handle this, but as fallback:
          if (result?.status === 'success' || result?.status === 'completed') {
            const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
            try {
              await axios.post('/api/bookings/create', {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: paymentData.phone,
                amount: amount,
                currency: currency,
                packageId: isManualPayment ? null : selectedPackageId,
                packageName: paymentData.packageName,
                transactionId: flywirePaymentId,
                paymentMethod: 'flywire'
              });
            } catch (e) {
              console.error('Booking creation in onComplete failed:', e);
            }
          }
        },
        // Handle payment cancellation
        onCancel: () => {
          console.log('❌ Flywire payment cancelled by user');
          setError('Payment was cancelled. Please try again when ready.');
          setProcessing(false);
        }
      };

      console.log('🚀 Flywire Config:', flywireConfig);
      console.log('Flywire Object Available:', typeof window.FlywirePayment);
      console.log('Initiate Function Available:', typeof window.FlywirePayment?.initiate);

      try {
        if (typeof window.FlywirePayment === 'undefined') {
          throw new Error('Flywire payment system not loaded. Please refresh the page.');
        }

        const modal = window.FlywirePayment.initiate(flywireConfig);
        console.log('Modal created:', modal);

        if (!modal) {
          throw new Error('Failed to initiate payment modal. Please try again.');
        }

        // Render the modal
        modal.render();
        console.log('Modal rendered successfully');
        setProcessing(false); // Reset processing state since modal is now shown

      } catch (modalError: any) {
        console.error('Modal creation error:', modalError);
        console.error('Error stack:', modalError.stack);
        setError(modalError.message || 'Failed to open payment modal. Please try again.');
        setProcessing(false);
        return;
      }

    } catch (err: any) {
      console.error('Payment initiation error:', err);
      setError(err.response?.data?.message || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  // Helper function to create booking record (non-blocking)
  const createBookingRecord = async (paymentData: any, amount: number, currency: string, transactionId: string) => {
    try {
      console.log('📝 Creating booking record...');
      const response = await axios.post('/api/bookings/manual', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: paymentData.phone,
        amount: amount,
        currency: currency,
        packageId: paymentData.packageId,
        packageName: paymentData.packageName,
        nationality: formData.country,
        paymentMethod: 'flywire',
        transactionId: transactionId
      });

      console.log('✅ Booking record created successfully:', response.data.booking.bookingCode);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create booking record:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg">Loading payment options...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Book Now - Secure Payment | Bali Getaway</title>
        <meta name="description" content="Complete your booking with secure payment processing" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 py-12 px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="container mx-auto"
        >
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-6xl font-black text-gray-900 mb-4 tracking-tight">
                Complete Your <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Booking</span>
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                Secure payment processing for your Bali adventure
              </p>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100">
              <div className="p-8 lg:p-12">
                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Error</p>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Payment Type Selection - Conditional Rendering */}
                  {shouldShowBothTabs() && (
                    <div className="space-y-4">
                      <label className="block text-lg font-bold text-gray-900 ml-1">
                        Select Booking Type
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {shouldShowPackageTab() && (
                          <button
                            type="button"
                            onClick={() => setIsManualPayment(false)}
                            className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${!isManualPayment
                              ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                              : 'border-gray-100 bg-white hover:border-orange-200'
                              }`}
                          >
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${!isManualPayment ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                              <span className="text-3xl">🎁</span>
                            </div>
                            <div>
                              <span className="block font-bold text-lg text-gray-900">Book Package</span>
                              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Pre-designed vacation packages</span>
                            </div>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setIsManualPayment(true)}
                          className={`p-6 rounded-3xl border-2 transition-all flex flex-col items-center gap-3 hover:shadow-md text-center ${isManualPayment
                            ? 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                            : 'border-gray-100 bg-white hover:border-orange-200'
                            } ${!shouldShowPackageTab() ? 'md:col-span-2' : ''}`}
                        >
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isManualPayment ? 'bg-white shadow-sm' : 'bg-gray-50'}`}>
                            <span className="text-3xl">💳</span>
                          </div>
                          <div>
                            <span className="block font-bold text-lg text-gray-900">Custom Payment</span>
                            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Personalized experiences & extras</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Package Selection or Manual Amount - Conditional based on availability */}
                  <div className="space-y-6">
                    {shouldShowPackageTab() && !isManualPayment ? (
                      <div className="space-y-6">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                            Select Package <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={selectedPackageId}
                            onChange={(e) => setSelectedPackageId(e.target.value)}
                            required
                            className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold"
                          >
                            <option value="">Choose a package...</option>
                            {packages.filter(pkg => pkg.bookEnabled).map((pkg) => {
                              // Check for active last minute deal
                              const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                              const dealPriceUSD = pkg.lastMinuteDealPriceUSD;
                              const hasDealPrice = currency === 'AUD'
                                ? dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD
                                : dealPriceUSD && dealPriceUSD > 0 && dealPriceUSD < pkg.priceUSD;
                              const isDealActive = pkg.lastMinuteDealExpiryDate
                                ? new Date(pkg.lastMinuteDealExpiryDate) > new Date()
                                : false;

                              const displayPrice = currency === 'USD'
                                ? (hasDealPrice && isDealActive && dealPriceUSD ? dealPriceUSD : pkg.priceUSD)
                                : (hasDealPrice && isDealActive && dealPriceAUD ? dealPriceAUD : pkg.priceAUD);

                              const dealLabel = hasDealPrice && isDealActive ? ' ⚡ DEAL' : '';

                              return (
                                <option key={pkg._id} value={pkg._id}>
                                  {pkg.name} - {currency} {displayPrice.toLocaleString()}{dealLabel}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Selected Price Type Indicator */}
                        {selectedPackageId && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-3"
                          >
                            <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                              Selected Price Type
                            </label>
                            <div className={`p-6 rounded-3xl border-2 transition-all ${selectedPriceType === 'deal'
                              ? 'border-red-500 bg-red-50 shadow-sm ring-4 ring-red-500/10'
                              : 'border-orange-500 bg-orange-50 shadow-sm ring-4 ring-orange-500/10'
                              }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${selectedPriceType === 'deal' ? 'bg-white' : 'bg-white'}`}>
                                    {selectedPriceType === 'deal' ? (
                                      <span className="text-2xl">⚡</span>
                                    ) : (
                                      <CreditCardIcon className="w-6 h-6 text-orange-600" />
                                    )}
                                  </div>
                                  <div>
                                    <span className={`block font-bold text-lg ${selectedPriceType === 'deal' ? 'text-red-700' : 'text-orange-700'}`}>
                                      {selectedPriceType === 'deal' ? 'Last Minute Deal' : 'Standard Price'}
                                    </span>
                                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Confirmed Selection</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="block text-2xl font-black text-gray-900">
                                    {currency} {getPaymentAmount().toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {selectedPriceType === 'deal' && (
                              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4">
                                <div className="flex gap-3">
                                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm font-black text-red-800 uppercase tracking-tight">⏰ Important Notice</p>
                                    <p className="text-xs text-red-700 mt-1 font-medium">
                                      This last minute deal requires booking within <span className="font-black underline">3 months from your original enquiry date</span>.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                          Enter Amount ({currency}) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={manualAmount}
                          onChange={(e) => setManualAmount(e.target.value)}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          required
                          className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold placeholder:text-gray-400"
                        />
                      </div>
                    )}
                  </div>

                  {/* Customer Details */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-gray-900 font-bold border-b border-gray-100 pb-2">
                      <UsersIcon className="w-5 h-5 text-orange-500" />
                      <h3>Guest Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                          placeholder="John"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 uppercase ml-1">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-500 uppercase ml-1">Phone Number *</label>
                      <div className="grid grid-cols-3 gap-3">
                        <select
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={(e) => {
                            handleInputChange(e);
                            const countryMap: { [key: string]: string } = {
                              '+61': 'AU', '+1': 'US', '+44': 'GB', '+91': 'IN'
                            };
                            setFormData(prev => ({
                              ...prev,
                              countryCode: e.target.value,
                              country: countryMap[e.target.value] || 'AU'
                            }));
                          }}
                          className="px-3 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 font-bold"
                        >
                          <option value="+61">🇦🇺 +61</option>
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+44">🇬🇧 +44</option>
                          <option value="+91">🇮🇳 +91</option>
                        </select>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="col-span-2 px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400 font-medium"
                          placeholder="412 345 678"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Summary */}
                  {getPaymentAmount() > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8 rounded-[2rem] border border-orange-100 shadow-sm relative overflow-hidden group">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                      <div className="flex justify-between items-center relative z-10">
                        <div>
                          <p className="text-xs font-black text-orange-400 uppercase tracking-widest mb-1">Total Amount Due</p>
                          <span className="text-4xl font-black text-orange-600">
                            {currency} {getPaymentAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                          <ShieldCheckIcon className="w-6 h-6 text-orange-500" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-bold text-orange-400/80 uppercase tracking-tighter mt-6 bg-white/50 w-fit px-3 py-1 rounded-full border border-orange-100/50">
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        <span>Encrypted & Secure Transaction</span>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={processing || (getPaymentAmount() <= 0)}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black py-5 px-8 rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-2xl shadow-orange-200 disabled:opacity-50 active:scale-[0.98] flex items-center justify-center gap-3 mt-4 tracking-wider uppercase"
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Initializing...</span>
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="w-6 h-6" />
                        <span>Proceed to Secure Payment</span>
                      </>
                    )}
                  </button>

                  <p className="text-center text-sm text-gray-600">
                    By proceeding, you agree to our Terms of Service and Privacy Policy
                  </p>
                </form>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default BookNowPage;
